<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import { VButton, VDescriptionList, VLoadingSection } from '@/modules/shared/components'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useInvestingInvestorStore } from '@/modules/investing/stores/investor-store'
import VSection from '@/modules/shared/components/v-section.vue'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { requestPasswordResetService } from '@/modules/auth/api'
import { rails_url } from '@/modules/shared/utils/rails'
import { format } from '@/modules/shared/utils/v-table'
import { format as formatDateFNS } from 'date-fns'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'

const route = useRoute()
const router = useRouter()
const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const workspaceStore = useWorkspaceStore()
const individualStore = useIndividualStore()
const currentIndividual = computed(() => individualStore.individual)

const any_new_membership_agreement = computed(
  () => currentIndividual.value?.membership_agreement_data?.group_agreement?.is_new,
)

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  hideSkeleton()
}

const sendRegistrationInvitation = async () => {
  await individualStore.resendInvitation(currentIndividual.value.id, { slug })
  alert(`${currentIndividual.value.name} has been sent a link to register`)
}

const request_password_reset = async () => {
  if (!window.confirm('Are you sure?')) return

  const payload = {
    email: currentIndividual.value.email,
  }
  const res = await requestPasswordResetService(payload)
  const { status } = res

  if (status == 'success') {
    alert('Thanks, check your email for instructions to reset your password')
    if (!authStore.is_site_or_group_admin) router.push({ name: 'logout' })
  }
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const is_viewing_own_profile = computed(
  () => authStore.current_user?.investor_id?.toString() === route.params.individual_id,
)
const isAdmin = computed(() => is_viewing_own_profile.value || authStore.is_site_or_group_admin)

onMounted(async () => {
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="login-information">
      <VSection
        v-if="!skeleton"
        label="Login Information"
        label_class="!font-semibold text-lg mb-3"
        class="!mb-0 rounded border-[1px] border-gray-300 p-3"
      >
        <template #actions v-if="isAdmin && currentIndividual && currentIndividual?.user">
          <div class="mb-2 flex gap-3">
            <VButton size="xs" variant="v-white" :click="request_password_reset" v-if="isAdmin">
              Reset Password
            </VButton>
          </div>
        </template>
        <template v-if="!currentIndividual?.user && isAdmin">
          <p v-if="currentIndividual.invitations_sent_count > 0" class="text-xs text-gray-500">
            Last registration email sent
            {{ formatDateFNS(new Date(currentIndividual.last_invited_at || new Date()), `EEEE, MMMM d, yyyy`) }}
          </p>
          <div class="mt-2 h-[1px] w-full bg-gray-300"></div>
          <div class="w-full space-y-5">
            <div class="border-[1px] bg-red-100 p-2 text-sm text-red-700 ring-red-200/75">
              {{ currentIndividual.name }} does not have a user account on Venture360 and therefore does not have access
              to this profile and investments.
            </div>
            <VButton
              size="sm"
              variant="primary"
              class="!h-fit !w-fit"
              :disabled="workspaceStore.current.setup_mode"
              :tooltip="'Not available in setup mode'"
              :click="sendRegistrationInvitation"
            >
              Resend registration invitation
            </VButton>
          </div>
        </template>
        <VDescriptionList
          :items="[
            {
              term: 'Created at',
              description: formatDateFNS(
                new Date(currentIndividual?.user?.created_at || new Date()),
                `EEEE, MMMM d, yyyy`,
              ),
            },
            {
              term: 'Last login at',
              description: currentIndividual?.last_login
                ? formatDateFNS(new Date(currentIndividual.last_login), `EEEE, MMMM d, Y 'at' h:mma`)
                : 'Never',
            },
          ]"
          :skeleton="skeleton"
          item_class="!mb-5"
          theme="form"
          v-else
        />
        <VDescriptionList
          :items="[
            {
              term: 'Membership Agreement',
              description: '',
            },
          ]"
          :skeleton="skeleton"
          item_class="!mb-5"
          theme="form"
          v-if="currentIndividual?.membership_agreement_data?.individual_agreement"
        >
          <template #description="{ item }">
            <!-- individual's agreement data -->
            <div class="ml-auto w-[80%] text-end">
              ✓ {{ is_viewing_own_profile ? 'You' : currentIndividual.name }} accepted the
              <a
                :href="currentIndividual.membership_agreement_data.individual_agreement.file_url"
                class="hyperlink"
                target="_blank"
              >
                {{ any_new_membership_agreement ? 'previous' : 'current' }} agreement
              </a>
              on
              {{ format(currentIndividual.membership_agreement_data.individual_agreement.date, 'date') }}
              {{ format(currentIndividual.membership_agreement_data.individual_agreement.date, 'time') }}
            </div>
          </template>
        </VDescriptionList>
      </VSection>
      <VLoadingSection
        v-else
        class="my-40"
        title="Preparing Your Information"
        description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
      smoothly."
      />
    </IndividualAccountLayout>
  </IndividualLayout>
</template>
